// src/pages/subscription/BundleListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionBundle, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { BundleFormDialog } from '@/features/subscription/BundleFormDialog';
import { formatMoney, humanizeEnum } from '@/features/subscription/format';

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'PRODUCT_BUNDLE_STATUS_DRAFT': return 'primary';
    case 'PRODUCT_BUNDLE_STATUS_ACTIVE': return 'success';
    case 'PRODUCT_BUNDLE_STATUS_SUSPENDED': return 'warning';
    case 'PRODUCT_BUNDLE_STATUS_ARCHIVED': return 'secondary';
    default: return 'secondary';
  }
}

export const BundleListPage = observer(() => {
  const store = useSubscriptionBundle();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    store.ListProductBundles({
      pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
      sort: [], view: 0,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try {
      await store.DeleteProductBundle(id);
      store.ListProductBundles({
        pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
        sort: [], view: 0,
      });
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="sr-only">Product Bundles</h1>
          <nav className="text-sm">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Bundles</span>
          </nav>
        </div>
        <Button onClick={() => setShowCreate(true)}>{t('subscription.bundle.create')}</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Billing Mode</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Subscriptions</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={9} message="No bundles found" />
            ) : (
              store.items.map((item: any) => {
                const itemId = item.entity?.id || item.id;
                return (
                  <TableRow key={itemId || Math.random()}>
                    <TableCell>
                      <Link to={`/subscriptions/bundles/${itemId}`} className="text-primary hover:underline">
                        {String(item.name ?? '')}
                      </Link>
                    </TableCell>
                    <TableCell>{String(item.slug ?? '--')}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(item.status)} size="sm">
                        {humanizeEnum(item.status, 'PRODUCT_BUNDLE_STATUS_')}
                      </Badge>
                    </TableCell>
                    <TableCell>{humanizeEnum(item.billing_mode, 'PRODUCT_BUNDLE_BILLING_MODE_')}</TableCell>
                    <TableCell>{formatMoney(item.bundle_price, item.currency)}</TableCell>
                    <TableCell>{String(item.discount_value ?? '--')}</TableCell>
                    <TableCell>{String(item.items_count ?? item.items?.length ?? 0)}</TableCell>
                    <TableCell>{String(item.current_subscriptions ?? 0)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/subscriptions/bundles/${itemId}`}><Button variant="ghost" size="sm">View</Button></Link>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(itemId)}>{t('common.delete')}</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <BundleFormDialog
        open={showCreate}
        bundle={null}
        onClose={() => setShowCreate(false)}
        onSaved={() => {
          store.ListProductBundles({
            pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
            sort: [], view: 0,
          });
        }}
      />
    </div>
  );
});

export default BundleListPage;
